import type { ReactElement } from 'react';

/**
 * What follows the pointer while things are dragged (a collection's `renderDragPreview`): the
 * first few, tilted over each other, and how many when more than one.
 *
 * Each layer is a picture filling its box; give it its own key. The browser takes its picture of
 * the stack as the drag starts, so a layer is drawn at once (an `img` with `loading="eager"` and
 * `decoding="sync"`), never faded in.
 */
export function DragStack({
  layers,
  count
}: {
  /** The dragged things' pictures, the first on top. */
  layers: ReactElement[];
  count: number;
}) {
  return (
    // room around the stack for the tilt, the ring, the shadow and the count: the browser's
    // picture of it is cut at its box
    <div data-drag-stack="" className="w-fit p-7">
      <div className="relative size-28">
        {layers
          .slice(0, 3)
          .reverse()
          .map((layer, index, shown) => {
            const depth = shown.length - 1 - index;
            return (
              <div
                key={layer.key ?? depth}
                data-drag-stack-part=""
                className="absolute inset-0 overflow-hidden rounded-lg bg-bg-subtle shadow-lg ring-2 ring-brand-500"
                style={{
                  transform: `translate(${depth * 6}px, ${depth * -6}px) rotate(${2 + depth * 4}deg)`
                }}
              >
                {layer}
              </div>
            );
          })}
        {count > 1 && (
          <span
            data-drag-stack-part=""
            className="absolute -top-2 -right-2 flex size-6 items-center justify-center rounded-full bg-brand-500 text-xs font-semibold text-white shadow"
          >
            {count}
          </span>
        )}
      </div>
    </div>
  );
}
